import React, { useMemo, useState } from 'react';
import { useTheme, Theme } from '../contexts/ThemeContext';
import { TimeSignature } from '../types';
import TimeSignatureEditorModal from './TimeSignatureEditorModal';
import ClockIcon from './icons/ClockIcon';

const SLOT_WIDTH = 32;

const createStyles = (theme: Theme) => ({
  container: {
    width: SLOT_WIDTH,
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    paddingLeft: theme.spacing.xs,
    paddingRight: theme.spacing.xs,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
  },
  value: {
    fontFamily: '"Times New Roman", Times, serif',
    fontSize: theme.typography.sizes.xl + 4,
    fontWeight: theme.typography.weights.semiBold,
    color: theme.colors.text,
    lineHeight: 1.1,
    textAlign: 'center' as const,
  },
  addAffordance: {
    border: `1px dashed ${theme.colors.border}`,
    borderRadius: theme.radii.sm,
    paddingLeft: theme.spacing.xs,
    paddingRight: theme.spacing.xs,
    paddingTop: 2,
    paddingBottom: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export interface TimeSignatureSlotProps {
  timeSignature: TimeSignature | null | undefined;
  isRowActive: boolean;
  onChange: (timeSignature: TimeSignature | null) => void;
}

const TimeSignatureSlot: React.FC<TimeSignatureSlotProps> = ({
  timeSignature,
  isRowActive,
  onChange,
}) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const [isEditorOpen, setIsEditorOpen] = useState(false);

  const hasValue = timeSignature != null;

  // Hide entirely when there's no value and the row is inactive,
  // so untouched rows look clean — no leading whitespace.
  if (!hasValue && !isRowActive) {
    return null;
  }

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setIsEditorOpen(true);
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        style={styles.container}
        aria-label={
          hasValue
            ? `Time signature ${timeSignature!.numerator} over ${timeSignature!.denominator}`
            : 'Add time signature'
        }
      >
        {hasValue ? (
          <>
            <span style={styles.value}>{timeSignature!.numerator}</span>
            <span style={styles.value}>{timeSignature!.denominator}</span>
          </>
        ) : (
          <span style={styles.addAffordance}>
            <ClockIcon size={14} color={theme.colors.textSecondary} />
          </span>
        )}
      </button>

      <TimeSignatureEditorModal
        isOpen={isEditorOpen}
        initialValue={timeSignature ?? null}
        onClose={() => setIsEditorOpen(false)}
        onApply={(value) => {
          onChange(value);
          setIsEditorOpen(false);
        }}
        onClear={() => {
          onChange(null);
          setIsEditorOpen(false);
        }}
      />
    </>
  );
};

export default TimeSignatureSlot;
